import React, { useEffect, useMemo, useRef, useState } from "react";
import Piano from "../components/Piano";
import {
  ROOT_NOTES,
  SCALES,
  CHORDS,
  pitchClassesFor,
  chordSemitones,
  chordNoteNames,
  diatonicTriads,
  rootToPc,
  noteNamesForKey,
  isFlatKey,
} from "../lib/music";
import { buildPianoSVGString, downloadSVGString } from "../lib/svgExport";
import { svgsToPdf } from "../lib/pdfExport";
import { playNotes, stopAll } from "../lib/audio";
import CircleOfFifths from "../components/CircleOfFifths";
import AuthButton from "../components/AuthButton";
import {
  loadPresets,
  addPreset,
  deletePreset,
} from "../lib/presets";
import {
  loadProgressions,
  addProgression,
  deleteProgression,
} from "../lib/progressions";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Download, Music, Settings2, Package, Save, Trash2, FileText, Play, Square, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { isScaleFree, isChordFree, isPaid, WATERMARK_TEXT } from "../lib/tier";

const PRESET_HIGHLIGHT = [
  "#3B82F6",
  "#EF4444",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#111827",
];

const PRESET_BG = [
  "#FFFFFF",
  "#F9FAFB",
  "#F3F4F6",
  "#FEF3C7",
  "#DBEAFE",
  "#111827",
];

// Scales that support diatonic triad generation (7-note scales)
const DIATONIC_SCALES = [
  "major",
  "natural_minor",
  "harmonic_minor",
  "melodic_minor",
  "dorian",
  "phrygian",
  "lydian",
  "mixolydian",
  "locrian",
];

export default function Visualizer() {
  const { user, openPaywall } = useAuth();
  const paid = isPaid(user);
  const watermark = paid ? null : WATERMARK_TEXT;

  const [mode, setMode] = useState("scale"); // scale | chord
  const [scaleRoot, setScaleRoot] = useState("C");
  const [scaleType, setScaleType] = useState("major");
  const [chordRoot, setChordRoot] = useState("C");
  const [chordType, setChordType] = useState("maj");
  const [inversion, setInversion] = useState(0);
  const [octaves, setOctaves] = useState(2);
  const [showLabels, setShowLabels] = useState(true);
  const [highlightColor, setHighlightColor] = useState("#3B82F6");
  const [bgColor, setBgColor] = useState("#FFFFFF");
  const [batchScaleRoot, setBatchScaleRoot] = useState("C");
  const [batchScaleType, setBatchScaleType] = useState("major");
  const [pdfMode, setPdfMode] = useState("scale"); // scale | chord
  const [pdfScaleType, setPdfScaleType] = useState("major");
  const [pdfChordType, setPdfChordType] = useState("maj");
  const [pdfBusy, setPdfBusy] = useState(false);
  const PDF_ROOTS = ["C", "Db", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];
  const [pdfSelectedRoots, setPdfSelectedRoots] = useState(new Set(PDF_ROOTS));
  const [pdfInversions, setPdfInversions] = useState(new Set([0]));
  const [pdfColumns, setPdfColumns] = useState(2);
  const [pdfPickedScales, setPdfPickedScales] = useState(new Set());
  const [pdfPickedChords, setPdfPickedChords] = useState(new Set());
  const [compareRoot, setCompareRoot] = useState("C");
  const [compareMode, setCompareMode] = useState("scale");
  const [compareTypes, setCompareTypes] = useState(new Set(["major"]));
  const [progression, setProgression] = useState([]);
  const [savedProgressions, setSavedProgressions] = useState([]);
  const [progressionName, setProgressionName] = useState("");
  const [selectedProgression, setSelectedProgression] = useState("");

  useEffect(() => {
    setSavedProgressions(loadProgressions());
  }, []);

  const handleSaveProgression = () => {
    const name = progressionName.trim();
    if (!name) {
      toast.error("Give your progression a name");
      return;
    }
    if (!progression.length) {
      toast.error("Add at least one chord");
      return;
    }
    const list = addProgression(name, progression);
    setSavedProgressions(list);
    setProgressionName("");
    setSelectedProgression(name);
    toast.success(`Progression "${name}" saved`);
  };

  const handleLoadProgression = (name) => {
    setSelectedProgression(name);
    const p = savedProgressions.find((x) => x.name === name);
    if (!p) return;
    setProgression(p.chords || []);
    toast.success(`Loaded "${name}"`);
  };

  const handleDeleteProgression = () => {
    if (!selectedProgression) return;
    const list = deleteProgression(selectedProgression);
    setSavedProgressions(list);
    setSelectedProgression("");
    toast.success("Deleted");
  };

  const handlePlayProgression = async () => {
    if (!progression.length) {
      toast.error("Add chords to your progression first");
      return;
    }
    for (let i = 0; i < progression.length; i++) {
      const c = progression[i];
      const rootPc = rootToPc(c.rootName);
      const iv =
        c.quality === "maj"
          ? [0, 4, 7]
          : c.quality === "min"
          ? [0, 3, 7]
          : [0, 3, 6];
      const midis = iv.map((s) => 60 + rootPc + s);
      await playNotes(midis, "chord", 180, null);
      await new Promise((r) => setTimeout(r, 1400));
    }
  };

  const handleCompareExport = async () => {
    setPdfBusy(true);
    try {
      const rootName = compareRoot;
      const rootPc = rootToPc(rootName);
      const names = noteNamesForKey(rootName);
      const keys = Array.from(compareTypes);
      if (!keys.length) {
        toast.error("Pick at least one scale/chord");
        setPdfBusy(false);
        return;
      }
      const items = keys.map((typeKey) => {
        const meta = compareMode === "scale" ? SCALES[typeKey] : CHORDS[typeKey];
        const iv = meta.intervals;
        const upper = rootPc + octaves * 12;
        const pcs = new Set(iv.map((x) => (rootPc + x) % 12));
        const offsets = new Set();
        for (let s = rootPc; s <= upper; s++) {
          if (pcs.has(s % 12)) offsets.add(s);
        }
        const maxSemi = Math.max(...offsets);
        const displayOct = Math.max(octaves, Math.floor(maxSemi / 12) + 1);
        const svgString = buildPianoSVGString({
          octaves: displayOct,
          highlightedOffsets: offsets,
          highlightColor,
          backgroundColor: bgColor,
          showLabels,
          noteNames: names,
        });
        return { svgString, subtitle: `${rootName} ${meta.label}` };
      });
      const title = `${rootName} — ${keys.length} ${compareMode === "scale" ? "scales" : "chords"}`;
      const safe = title.replace(/[^a-zA-Z0-9]+/g, "_");
      await svgsToPdf([{ title, items }], `${safe}.pdf`, pdfColumns, { watermark });
      toast.success("PDF downloaded", { description: `${items.length} diagrams` });
    } catch (e) {
      toast.error("PDF export failed", { description: String(e.message || e) });
    } finally {
      setPdfBusy(false);
    }
  };
  const [tempo, setTempo] = useState(180);
  const [playingSemis, setPlayingSemis] = useState(null);

  // Guarded setters for scale/chord type — pop the paywall on locked picks.
  const chooseScaleType = (v) => {
    if (!paid && !isScaleFree(v)) {
      toast.info(`${SCALES[v]?.label || v} is a paid scale`, {
        description: "Unlock lifetime access to use all 15 scales",
      });
      openPaywall();
      return;
    }
    setScaleType(v);
  };
  const chooseChordType = (v) => {
    if (!paid && !isChordFree(v)) {
      toast.info(`${CHORDS[v]?.label || v} is a paid chord`, {
        description: "Unlock lifetime access to use all 24 chord qualities",
      });
      openPaywall();
      return;
    }
    setChordType(v);
  };
  const choosePdfScaleType = (v) => {
    if (!paid && v !== "_ALL_" && !isScaleFree(v)) {
      openPaywall();
      return;
    }
    if (!paid && v === "_ALL_") {
      toast.info("All scale types export is a paid feature");
      openPaywall();
      return;
    }
    setPdfScaleType(v);
  };
  const choosePdfChordType = (v) => {
    if (!paid && v !== "_ALL_" && !isChordFree(v)) {
      openPaywall();
      return;
    }
    if (!paid && v === "_ALL_") {
      toast.info("All chord types export is a paid feature");
      openPaywall();
      return;
    }
    setPdfChordType(v);
  };
  const toggleCompareType = (k) => {
    const isFree = compareMode === "scale" ? isScaleFree(k) : isChordFree(k);
    if (!paid && !isFree && !compareTypes.has(k)) {
      openPaywall();
      return;
    }
    setCompareTypes(toggleSetItem(compareTypes, k));
  };

  const toggleSetItem = (set, item) => {
    const next = new Set(set);
    if (next.has(item)) next.delete(item);
    else next.add(item);
    return next;
  };
  const rootPresets = {
    all: PDF_ROOTS,
    white: ["C", "D", "E", "F", "G", "A", "B"],
    black: ["Db", "Eb", "F#", "Ab", "Bb"],
    sharp: ["C", "G", "D", "A", "E", "B", "F#"],
    flat: ["F", "Bb", "Eb", "Ab", "Db", "Gb"],
  };
  const [presets, setPresets] = useState([]);
  const [newPresetName, setNewPresetName] = useState("");
  const [selectedPreset, setSelectedPreset] = useState("");

  useEffect(() => {
    setPresets(loadPresets());
  }, []);

  const handleSavePreset = () => {
    const name = newPresetName.trim();
    if (!name) {
      toast.error("Give your preset a name");
      return;
    }
    const preset = {
      name,
      highlightColor,
      bgColor,
      showLabels,
      octaves,
    };
    const list = addPreset(preset);
    setPresets(list);
    setSelectedPreset(name);
    setNewPresetName("");
    toast.success(`Preset "${name}" saved`);
  };

  const handleLoadPreset = (name) => {
    setSelectedPreset(name);
    const p = presets.find((x) => x.name === name);
    if (!p) return;
    setHighlightColor(p.highlightColor);
    setBgColor(p.bgColor);
    setShowLabels(p.showLabels);
    setOctaves(p.octaves);
    toast.success(`Preset "${name}" applied`);
  };

  const handleDeletePreset = () => {
    if (!selectedPreset) return;
    const list = deletePreset(selectedPreset);
    setPresets(list);
    const name = selectedPreset;
    setSelectedPreset("");
    toast.success(`Preset "${name}" deleted`);
  };

  const svgRef = useRef(null);

  // Reset inversion when chord changes size below current inversion
  const chordSize = CHORDS[chordType].intervals.length;
  const effectiveInversion = Math.min(inversion, Math.max(0, chordSize - 1));

  // Piano keyboard always starts at C. Only highlights change per scale/chord.
  const startingPc = 0;

  // Note names for current key (sharp or flat spelling)
  const currentRoot = mode === "scale" ? scaleRoot : chordRoot;
  const noteNames = useMemo(() => noteNamesForKey(currentRoot), [currentRoot]);
  const useFlats = useMemo(() => isFlatKey(currentRoot), [currentRoot]);

  // Highlighted semitone offsets from C (the leftmost key).
  // Highlights start at the root note's first occurrence AND end at the same
  // root note one/multiple octaves higher (inclusive on both ends).
  const { highlightedOffsets, chordVoicingNames } = useMemo(() => {
    if (mode === "scale") {
      const rootPc = rootToPc(scaleRoot);
      const pcs = pitchClassesFor(scaleRoot, SCALES[scaleType].intervals);
      const upper = rootPc + octaves * 12;
      const offsets = new Set();
      for (let s = rootPc; s <= upper; s++) {
        if (pcs.has(s % 12)) offsets.add(s);
      }
      return { highlightedOffsets: offsets, chordVoicingNames: null };
    }
    const chordRootPc = rootToPc(chordRoot);
    const semisFromRoot = chordSemitones(
      CHORDS[chordType].intervals,
      effectiveInversion
    );
    if (effectiveInversion === 0) {
      // Root position: highlight ONE instance per chord tone (C, E, G — not C, E, G, C)
      return {
        highlightedOffsets: new Set(semisFromRoot.map((s) => s + chordRootPc)),
        chordVoicingNames: chordNoteNames(semisFromRoot, chordRootPc, useFlats),
      };
    }
    // Inversion voicing: exactly one instance per chord tone
    return {
      highlightedOffsets: new Set(semisFromRoot.map((s) => s + chordRootPc)),
      chordVoicingNames: chordNoteNames(semisFromRoot, chordRootPc, useFlats),
    };
  }, [
    mode,
    scaleRoot,
    scaleType,
    chordRoot,
    chordType,
    effectiveInversion,
    octaves,
    useFlats,
  ]);

  // For display: the scale/chord pitch classes (used to show note names below the title)
  const displayedPCs = useMemo(() => {
    if (mode === "scale") {
      return pitchClassesFor(scaleRoot, SCALES[scaleType].intervals);
    }
    return pitchClassesFor(chordRoot, CHORDS[chordType].intervals);
  }, [mode, scaleRoot, scaleType, chordRoot, chordType]);

  const titleText = useMemo(() => {
    if (mode === "scale") {
      return `${scaleRoot} ${SCALES[scaleType].label}`;
    }
    const invLabel =
      effectiveInversion === 0
        ? ""
        : effectiveInversion === 1
        ? " — 1st Inv"
        : effectiveInversion === 2
        ? " — 2nd Inv"
        : ` — ${effectiveInversion}rd Inv`;
    return `${chordRoot} ${CHORDS[chordType].label}${invLabel}`;
  }, [mode, scaleRoot, scaleType, chordRoot, chordType, effectiveInversion]);

  // Auto-expand piano octaves so all highlighted keys are visible (both for
  // scale mode where highlights end on the root above, and for chord inversions).
  const displayOctaves = useMemo(() => {
    if (!highlightedOffsets || highlightedOffsets.size === 0) return octaves;
    let maxSemi = 0;
    highlightedOffsets.forEach((s) => {
      if (s > maxSemi) maxSemi = s;
    });
    return Math.max(octaves, Math.floor(maxSemi / 12) + 1);
  }, [highlightedOffsets, octaves]);

  const handleDownloadSVG = async () => {
    const svg = buildPianoSVGString({
      octaves: displayOctaves,
      startingPc,
      highlightedPCs: null,
      highlightedOffsets,
      highlightColor,
      backgroundColor: bgColor,
      showLabels,
      noteNames,
    });
    const safe = titleText.replace(/[^a-zA-Z0-9]+/g, "_");
    try {
      await svgsToPdf([{ title: titleText, items: [{ svgString: svg, subtitle: "" }] }], `${safe}.pdf`, 1, { watermark });
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  const handlePdfExport = async () => {
    setPdfBusy(true);
    try {
      const typeKeys =
        pdfMode === "scale"
          ? pdfScaleType === "_ALL_"
            ? Object.keys(SCALES)
            : [pdfScaleType]
          : pdfChordType === "_ALL_"
          ? Object.keys(CHORDS)
          : [pdfChordType];

      const rootsList = PDF_ROOTS.filter((r) => pdfSelectedRoots.has(r));
      const invList = pdfMode === "chord" ? Array.from(pdfInversions).sort() : [0];

      // Helper to build one SVG item for a (root, type, inv) combo
      const buildItem = (rootName, typeKey, inv) => {
        const meta = pdfMode === "scale" ? SCALES[typeKey] : CHORDS[typeKey];
        const rootPc = rootToPc(rootName);
        const names = noteNamesForKey(rootName);
        const iv = meta.intervals;
        let offsets;
        if (pdfMode === "chord" && inv > 0) {
          const semis = chordSemitones(iv, inv);
          offsets = new Set(semis.map((s) => s + rootPc));
        } else {
          const upper = rootPc + octaves * 12;
          const pcs = new Set(iv.map((x) => (rootPc + x) % 12));
          offsets = new Set();
          for (let s = rootPc; s <= upper; s++) {
            if (pcs.has(s % 12)) offsets.add(s);
          }
        }
        const maxSemi = Math.max(...offsets);
        const displayOct = Math.max(octaves, Math.floor(maxSemi / 12) + 1);
        const svgString = buildPianoSVGString({
          octaves: displayOct,
          highlightedOffsets: offsets,
          highlightColor,
          backgroundColor: bgColor,
          showLabels,
          noteNames: names,
        });
        return { svgString, label: meta.label, invLabel: inv > 0 ? ` — Inv ${inv}` : "" };
      };

      let groups;
      // Special layout: single root + multiple types → one page titled by root,
      // items labeled by scale/chord type. Matches "C Major, C Lydian, C Dorian…" use case.
      if (rootsList.length === 1 && typeKeys.length > 1) {
        const rootName = rootsList[0];
        const items = [];
        for (const typeKey of typeKeys) {
          for (const inv of invList) {
            const b = buildItem(rootName, typeKey, inv);
            items.push({ svgString: b.svgString, subtitle: `${rootName} ${b.label}${b.invLabel}` });
          }
        }
        groups = [{ title: `${rootName} — ${typeKeys.length} ${pdfMode === "scale" ? "scales" : "chords"}`, items }];
      } else {
        groups = typeKeys.map((typeKey) => {
          const items = [];
          for (const rootName of rootsList) {
            for (const inv of invList) {
              const b = buildItem(rootName, typeKey, inv);
              items.push({ svgString: b.svgString, subtitle: `${rootName}${b.invLabel}` });
            }
          }
          const meta = pdfMode === "scale" ? SCALES[typeKey] : CHORDS[typeKey];
          return { title: meta.label, items };
        });
      }

      const label =
        pdfMode === "scale"
          ? pdfScaleType === "_ALL_"
            ? "All_Scales"
            : SCALES[pdfScaleType].label
          : pdfChordType === "_ALL_"
          ? "All_Chords"
          : CHORDS[pdfChordType].label;
      const safe = label.replace(/[^a-zA-Z0-9]+/g, "_");
      await svgsToPdf(groups, `All_Roots_${safe}.pdf`, pdfColumns, { watermark });
      toast.success("PDF downloaded", { description: `${groups.length} page(s)` });
    } catch (e) {
      toast.error("PDF export failed", { description: String(e.message || e) });
    } finally {
      setPdfBusy(false);
    }
  };

  const handleBatchExport = async () => {
    const chords = diatonicTriads(
      rootToPc(batchScaleRoot),
      batchScaleType,
      isFlatKey(batchScaleRoot)
    );
    if (!chords.length) {
      toast.error("Pick a 7-note scale for batch export");
      return;
    }
    const names = noteNamesForKey(batchScaleRoot);
    const items = chords.map((c) => {
      const chordPcs = pitchClassesFor(c.rootName, c.intervals);
      const upper = c.rootPc + octaves * 12;
      const chordOffsets = new Set();
      for (let s = c.rootPc; s <= upper; s++) {
        if (chordPcs.has(s % 12)) chordOffsets.add(s);
      }
      const displayOct = Math.max(octaves, Math.floor(upper / 12) + 1);
      const svgString = buildPianoSVGString({
        octaves: displayOct,
        highlightedOffsets: chordOffsets,
        highlightColor,
        backgroundColor: bgColor,
        showLabels,
        noteNames: names,
      });
      return { svgString, subtitle: `${c.roman}  ${c.label}` };
    });
    const title = `${batchScaleRoot} ${SCALES[batchScaleType].label} — diatonic chords`;
    const safe = title.replace(/[^a-zA-Z0-9]+/g, "_");
    try {
      await svgsToPdf([{ title, items }], `${safe}.pdf`, pdfColumns, { watermark });
      toast.success("PDF downloaded", { description: `${items.length} chords, 1 page` });
    } catch (e) {
      toast.error("PDF export failed", { description: String(e.message || e) });
    }
  };

  const sectionLabel =
    "uppercase text-xs tracking-[0.2em] font-bold text-gray-500";

  const batchChords = useMemo(
    () =>
      diatonicTriads(rootToPc(batchScaleRoot), batchScaleType, isFlatKey(batchScaleRoot)),
    [batchScaleRoot, batchScaleType]
  );

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="border-b-2 border-gray-900 px-6 md:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-14 h-10 rounded-md bg-gray-900 flex items-center justify-center p-1.5">
            <svg
              viewBox="0 0 60 40"
              width="44"
              height="30"
              xmlns="http://www.w3.org/2000/svg"
              aria-label="KEYVISION mini piano logo"
            >
              {/* White keys: C, D, E */}
              <rect x="1" y="1" width="18" height="38" fill="#FFFFFF" stroke="#111827" strokeWidth="1.5" />
              <rect x="21" y="1" width="18" height="38" fill="#673A80" stroke="#111827" strokeWidth="1.5" />
              <rect x="41" y="1" width="18" height="38" fill="#FFFFFF" stroke="#111827" strokeWidth="1.5" />
              {/* Black keys between C-D and D-E */}
              <rect x="13" y="1" width="8" height="24" fill="#111827" />
              <rect x="39" y="1" width="8" height="24" fill="#111827" />
            </svg>
          </div>
          <div>
            <div
              className="font-heading font-semibold tracking-tight text-xl leading-none"
              style={{
                fontFamily: "'Inter', sans-serif",
              }}
            >
              KEYVISION
            </div>
            <div className="text-xs text-gray-500 tracking-widest uppercase mt-0.5">
              by Piano Calling
            </div>
          </div>
        </div>
        <AuthButton />
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-0">
        {/* Sidebar */}
        <aside className="col-span-1 border-r-2 border-gray-900 p-6 md:p-8 flex flex-col gap-8 bg-[#F9FAFB] min-h-[calc(100vh-89px)]">
          <div className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-gray-700" />
            <h2
              className="text-sm uppercase tracking-[0.2em] font-bold text-gray-900"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Controls
            </h2>
          </div>

          <Tabs value={mode} onValueChange={setMode} className="w-full">
            <TabsList className="grid grid-cols-2 w-full rounded-xl border-2 border-gray-900 bg-white p-0 h-12">
              <TabsTrigger
                value="scale"
                data-testid="mode-tab-scale"
                className="rounded-xl data-[state=active]:bg-gray-900 data-[state=active]:text-white text-sm font-bold uppercase tracking-wider h-full"
              >
                Scale
              </TabsTrigger>
              <TabsTrigger
                value="chord"
                data-testid="mode-tab-chord"
                className="rounded-xl data-[state=active]:bg-gray-900 data-[state=active]:text-white text-sm font-bold uppercase tracking-wider h-full"
              >
                Chord
              </TabsTrigger>
            </TabsList>

            <TabsContent value="scale" className="mt-6 flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <Label className={sectionLabel}>Root note</Label>
                <Select value={scaleRoot} onValueChange={setScaleRoot}>
                  <SelectTrigger
                    data-testid="scale-root-select"
                    className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {ROOT_NOTES.map((n) => (
                      <SelectItem key={n} value={n}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label className={sectionLabel}>Scale type</Label>
                <Select value={scaleType} onValueChange={chooseScaleType}>
                  <SelectTrigger
                    data-testid="scale-type-select"
                    className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl max-h-72">
                    {Object.entries(SCALES).map(([key, val]) => {
                      const locked = !paid && !isScaleFree(key);
                      return (
                        <SelectItem
                          key={key}
                          value={key}
                          data-testid={`scale-type-item-${key}`}
                          className={locked ? "text-gray-400" : ""}
                        >
                          <span className="inline-flex items-center gap-2">
                            {val.label}
                            {locked && (
                              <Lock className="w-3 h-3 text-[#673A80]" />
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="chord" className="mt-6 flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <Label className={sectionLabel}>Root note</Label>
                <Select value={chordRoot} onValueChange={setChordRoot}>
                  <SelectTrigger
                    data-testid="chord-root-select"
                    className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {ROOT_NOTES.map((n) => (
                      <SelectItem key={n} value={n}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label className={sectionLabel}>Chord type</Label>
                <Select value={chordType} onValueChange={chooseChordType}>
                  <SelectTrigger
                    data-testid="chord-type-select"
                    className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl max-h-72">
                    {Object.entries(CHORDS).map(([key, val]) => {
                      const locked = !paid && !isChordFree(key);
                      return (
                        <SelectItem
                          key={key}
                          value={key}
                          data-testid={`chord-type-item-${key}`}
                          className={locked ? "text-gray-400" : ""}
                        >
                          <span className="inline-flex items-center gap-2">
                            {val.label}
                            {locked && (
                              <Lock className="w-3 h-3 text-[#673A80]" />
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label className={sectionLabel}>Inversion</Label>
                <Select
                  value={String(effectiveInversion)}
                  onValueChange={(v) => setInversion(parseInt(v, 10))}
                >
                  <SelectTrigger
                    data-testid="inversion-select"
                    className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {Array.from({ length: chordSize }).map((_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {i === 0
                          ? "Root position"
                          : i === 1
                          ? "1st inversion"
                          : i === 2
                          ? "2nd inversion"
                          : `${i}rd inversion`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
          </Tabs>

          <div className="h-px bg-gray-200" />

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Label className={sectionLabel}>Octaves</Label>
              <span
                className="font-mono text-sm font-bold text-gray-900"
                data-testid="octave-value"
              >
                {octaves}
              </span>
            </div>
            <Slider
              data-testid="octave-slider"
              value={[octaves]}
              onValueChange={(v) => setOctaves(v[0])}
              min={1}
              max={4}
              step={1}
              className="mt-1"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className={sectionLabel}>Note labels</Label>
            <Switch
              data-testid="labels-toggle"
              checked={showLabels}
              onCheckedChange={setShowLabels}
            />
          </div>

          <div className="flex flex-col gap-3">
            <Label className={sectionLabel}>Highlight color</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {PRESET_HIGHLIGHT.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`highlight ${c}`}
                  onClick={() => setHighlightColor(c)}
                  className={`w-8 h-8 border-2 ${
                    highlightColor === c
                      ? "border-gray-900"
                      : "border-gray-300"
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <label className="relative w-8 h-8 border-2 border-gray-300 cursor-pointer overflow-hidden">
                <input
                  data-testid="highlight-color-picker"
                  type="color"
                  value={highlightColor}
                  onChange={(e) => setHighlightColor(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
                <div
                  className="w-full h-full"
                  style={{ backgroundColor: highlightColor }}
                />
              </label>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Label className={sectionLabel}>Background</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {PRESET_BG.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`background ${c}`}
                  onClick={() => setBgColor(c)}
                  className={`w-8 h-8 border-2 ${
                    bgColor === c ? "border-gray-900" : "border-gray-300"
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <label className="relative w-8 h-8 border-2 border-gray-300 cursor-pointer overflow-hidden">
                <input
                  data-testid="bg-color-picker"
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
                <div
                  className="w-full h-full"
                  style={{ backgroundColor: bgColor }}
                />
              </label>
            </div>
          </div>

          {/* Presets */}
          <div className="h-px bg-gray-200" />

          <div className="flex flex-col gap-3" data-testid="presets-section">
            <div className="flex items-center gap-2">
              <Save className="w-3.5 h-3.5 text-gray-700" />
              <Label className={sectionLabel}>Brand presets</Label>
            </div>

            {presets.length > 0 && (
              <div className="flex items-center gap-2">
                <Select
                  value={selectedPreset}
                  onValueChange={handleLoadPreset}
                >
                  <SelectTrigger
                    data-testid="preset-select"
                    className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-11 bg-white flex-1"
                  >
                    <SelectValue placeholder="Load a preset…" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {presets.map((p) => (
                      <SelectItem key={p.name} value={p.name}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  data-testid="preset-delete-button"
                  onClick={handleDeletePreset}
                  disabled={!selectedPreset}
                  variant="outline"
                  className="rounded-xl border-2 border-gray-300 h-11 px-3"
                  aria-label="Delete preset"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Input
                data-testid="preset-name-input"
                placeholder="New preset name…"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-11 flex-1"
              />
              <Button
                data-testid="preset-save-button"
                onClick={handleSavePreset}
                className="rounded-xl bg-gray-900 hover:bg-gray-700 text-white h-11 px-4 font-medium"
              >
                Save
              </Button>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Saves your current highlight color, background, note-label
              setting, and octave count — reapply any time in one click.
            </p>
          </div>
        </aside>

        {/* Stage */}
        <main className="col-span-1 lg:col-span-3 p-6 md:p-10 flex flex-col gap-6">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">
                Now showing
              </div>
              <h1
                data-testid="visualizer-title"
                className="text-4xl sm:text-5xl font-semibold tracking-tight text-gray-900 leading-none"
                style={{
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {titleText}
              </h1>
              <p
                className="text-sm text-gray-500 mt-3"
                data-testid="voicing-notes"
              >
                {mode === "chord" && chordVoicingNames
                  ? chordVoicingNames.map((n, i) => (
                      <span key={i} className="font-mono font-bold mr-2">
                        {n}
                      </span>
                    ))
                  : (() => {
                      const rootPc = rootToPc(currentRoot);
                      const iv = SCALES[scaleType].intervals;
                      const seq = [...iv, 12].map((i) => (rootPc + i) % 12);
                      return seq.map((pc, i) => (
                        <span key={i} className="font-mono font-bold mr-2">
                          {noteNames[pc]}
                        </span>
                      ));
                    })()}
              </p>
            </div>
          </div>

          <div
            className="border-2 border-gray-900 flex items-center justify-center p-6 md:p-10 overflow-x-auto"
            style={{ backgroundColor: bgColor }}
          >
            <Piano
              ref={svgRef}
              octaves={displayOctaves}
              startingPc={startingPc}
              highlightedOffsets={highlightedOffsets}
              highlightColor={highlightColor}
              backgroundColor={bgColor}
              showLabels={showLabels}
              noteNames={noteNames}
              playingSemis={playingSemis}
            />
          </div>

          <div className="flex justify-end items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 mr-auto">
              <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Tempo</Label>
              <div className="w-40">
                <Slider
                  data-testid="tempo-slider"
                  value={[tempo]}
                  onValueChange={(v) => setTempo(v[0])}
                  min={60}
                  max={300}
                  step={5}
                />
              </div>
              <span data-testid="tempo-value" className="font-mono text-sm font-bold w-16">{tempo} BPM</span>
            </div>
            <Button
              data-testid="play-button"
              onClick={async () => {
                const base = 60;
                const offsets = Array.from(highlightedOffsets).sort((a, b) => a - b);
                const midis = offsets.map((s) => base + s);
                await playNotes(
                  midis,
                  mode === "chord" ? "chord" : "scale",
                  tempo,
                  (playingMidi) => {
                    if (playingMidi === null) {
                      setPlayingSemis(null);
                      return;
                    }
                    if (Array.isArray(playingMidi)) {
                      setPlayingSemis(new Set(playingMidi.map((m) => m - base)));
                    } else {
                      setPlayingSemis(new Set([playingMidi - base]));
                    }
                  }
                );
              }}
              variant="outline"
              className="rounded-xl border-2 border-gray-900 h-12 px-6 font-medium"
            >
              <Play className="w-4 h-4 mr-2" />
              Play
            </Button>
            <Button
              data-testid="stop-button"
              onClick={() => { stopAll(); setPlayingSemis(null); }}
              variant="outline"
              className="rounded-xl border-2 border-gray-900 h-12 px-4"
              aria-label="Stop"
            >
              <Square className="w-4 h-4" />
            </Button>
            <Button
              data-testid="export-svg-button"
              onClick={handleDownloadSVG}
              className="rounded-xl bg-gray-900 hover:bg-gray-700 text-white h-12 px-6 font-medium"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>

          {/* Batch export section */}
          <section
            className="rounded-2xl border-2 border-gray-900 bg-white p-6 md:p-8 mt-4"
            data-testid="batch-export-panel"
          >
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-4 h-4 text-gray-700" />
              <h2
                className="text-sm uppercase tracking-[0.2em] font-bold text-gray-900"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Batch export — diatonic chords
              </h2>
            </div>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed max-w-2xl">
              Generate one SVG per diatonic chord (I, ii, iii, IV, V, vi, vii°)
              in the selected key. Perfect for building a full slide pack for a
              lesson in one click.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div className="flex flex-col gap-2">
                <Label className={sectionLabel}>Key root</Label>
                <Select
                  value={batchScaleRoot}
                  onValueChange={setBatchScaleRoot}
                >
                  <SelectTrigger
                    data-testid="batch-root-select"
                    className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {ROOT_NOTES.map((n) => (
                      <SelectItem key={n} value={n}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label className={sectionLabel}>Scale</Label>
                <Select
                  value={batchScaleType}
                  onValueChange={setBatchScaleType}
                >
                  <SelectTrigger
                    data-testid="batch-scale-select"
                    className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl max-h-72">
                    {DIATONIC_SCALES.map((k) => (
                      <SelectItem key={k} value={k}>
                        {SCALES[k].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                data-testid="batch-export-button"
                onClick={handleBatchExport}
                className="rounded-xl bg-gray-900 hover:bg-gray-700 text-white h-12 font-medium w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Export {batchChords.length} SVGs
              </Button>
            </div>

            {batchChords.length > 0 && (
              <div
                className="mt-6 flex flex-wrap gap-2"
                data-testid="batch-chords-preview"
              >
                {batchChords.map((c, i) => (
                  <div
                    key={i}
                    className="border-2 border-gray-300 px-3 py-2 flex items-baseline gap-2 bg-[#F9FAFB]"
                  >
                    <span className="font-mono text-xs tracking-widest text-gray-500">
                      {c.roman}
                    </span>
                    <span className="font-bold text-gray-900">{c.label}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* PDF Reference Sheet */}
          <section
            className="rounded-2xl border-2 border-gray-900 bg-white p-6 md:p-8"
            data-testid="pdf-panel"
          >
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-gray-700" />
              <h2
                className="text-sm uppercase tracking-[0.2em] font-bold text-gray-900"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Reference sheet PDF (all 12 roots)
              </h2>
            </div>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed max-w-2xl">
              Pick a scale type OR chord quality and download a single PDF with
              one page per root (C, Db, D, Eb, E, F, F#, G, Ab, A, Bb, B).
              Perfect as a printable cheat sheet for your students.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div className="flex flex-col gap-2">
                <Label className={sectionLabel}>Type</Label>
                <Select value={pdfMode} onValueChange={setPdfMode}>
                  <SelectTrigger
                    data-testid="pdf-mode-select"
                    className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="scale">Scale</SelectItem>
                    <SelectItem value="chord">Chord</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {pdfMode === "scale" ? (
                <div className="flex flex-col gap-2">
                  <Label className={sectionLabel}>Scale</Label>
                  <Select
                    value={pdfScaleType}
                    onValueChange={choosePdfScaleType}
                  >
                    <SelectTrigger
                      data-testid="pdf-scale-select"
                      className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl max-h-72">
                      <SelectItem value="_ALL_">
                        <span className="inline-flex items-center gap-2">
                          All scale types (one PDF)
                          {!paid && <Lock className="w-3 h-3 text-[#673A80]" />}
                        </span>
                      </SelectItem>
                      {Object.entries(SCALES).map(([k, v]) => {
                        const locked = !paid && !isScaleFree(k);
                        return (
                          <SelectItem
                            key={k}
                            value={k}
                            className={locked ? "text-gray-400" : ""}
                          >
                            <span className="inline-flex items-center gap-2">
                              {v.label}
                              {locked && (
                                <Lock className="w-3 h-3 text-[#673A80]" />
                              )}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <Label className={sectionLabel}>Chord</Label>
                  <Select
                    value={pdfChordType}
                    onValueChange={choosePdfChordType}
                  >
                    <SelectTrigger
                      data-testid="pdf-chord-select"
                      className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl max-h-72">
                      <SelectItem value="_ALL_">
                        <span className="inline-flex items-center gap-2">
                          All chord types (one PDF)
                          {!paid && <Lock className="w-3 h-3 text-[#673A80]" />}
                        </span>
                      </SelectItem>
                      {Object.entries(CHORDS).map(([k, v]) => {
                        const locked = !paid && !isChordFree(k);
                        return (
                          <SelectItem
                            key={k}
                            value={k}
                            className={locked ? "text-gray-400" : ""}
                          >
                            <span className="inline-flex items-center gap-2">
                              {v.label}
                              {locked && (
                                <Lock className="w-3 h-3 text-[#673A80]" />
                              )}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                data-testid="pdf-export-button"
                onClick={handlePdfExport}
                disabled={pdfBusy}
                className="rounded-xl bg-gray-900 hover:bg-gray-700 text-white h-12 font-medium w-full"
              >
                <FileText className="w-4 h-4 mr-2" />
                {pdfBusy ? "Building…" : "Download PDF"}
              </Button>
            </div>

            {/* Columns selector */}
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <Label className={sectionLabel}>Columns per page</Label>
              {[1, 2].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`pdf-cols-${n}`}
                  onClick={() => setPdfColumns(n)}
                  className={`px-3 py-1 border-2 font-mono font-bold text-sm ${
                    pdfColumns === n
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-900 border-gray-300"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>


            {/* Root chips + presets */}
            <div className="mt-6 flex flex-col gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Label className={sectionLabel}>Roots</Label>
                {Object.entries(rootPresets).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`pdf-preset-${k}`}
                    onClick={() => setPdfSelectedRoots(new Set(v))}
                    className="text-xs border-2 border-gray-300 hover:border-gray-900 px-2 py-1 font-mono uppercase"
                  >
                    {k}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="pdf-preset-clear"
                  onClick={() => setPdfSelectedRoots(new Set())}
                  className="text-xs border-2 border-gray-300 hover:border-gray-900 px-2 py-1 font-mono uppercase"
                >
                  clear
                </button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {PDF_ROOTS.map((r) => {
                  const on = pdfSelectedRoots.has(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      data-testid={`pdf-root-${r}`}
                      onClick={() =>
                        setPdfSelectedRoots(toggleSetItem(pdfSelectedRoots, r))
                      }
                      className={`min-w-[44px] px-2 py-1 border-2 font-mono font-bold text-sm ${
                        on
                          ? "bg-gray-900 text-white border-gray-900"
                          : "bg-white text-gray-900 border-gray-300"
                      }`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
              {pdfMode === "chord" && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Label className={sectionLabel}>Inversions</Label>
                  {[0, 1, 2, 3].map((inv) => {
                    const on = pdfInversions.has(inv);
                    const label =
                      inv === 0 ? "Root" : `${inv}${inv === 1 ? "st" : inv === 2 ? "nd" : "rd"}`;
                    return (
                      <button
                        key={inv}
                        type="button"
                        data-testid={`pdf-inv-${inv}`}
                        onClick={() =>
                          setPdfInversions(toggleSetItem(pdfInversions, inv))
                        }
                        className={`px-3 py-1 border-2 font-mono font-bold text-sm ${
                          on
                            ? "bg-gray-900 text-white border-gray-900"
                            : "bg-white text-gray-900 border-gray-300"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Compare on one root — dedicated panel */}
          <section
            className="rounded-2xl border-2 border-gray-900 bg-white p-6 md:p-8"
            data-testid="compare-panel"
          >
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-gray-700" />
              <h2
                className="text-sm uppercase tracking-[0.2em] font-bold text-gray-900"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Compare on ONE root
              </h2>
            </div>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed max-w-2xl">
              Pick a single root and any number of scales or chord qualities —
              get a PDF that shows all of them on that same root (e.g. C Major,
              C Lydian, C Dorian on one page).
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end mb-4">
              <div className="flex flex-col gap-2">
                <Label className={sectionLabel}>Root</Label>
                <Select value={compareRoot} onValueChange={setCompareRoot}>
                  <SelectTrigger
                    data-testid="compare-root-select"
                    className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {ROOT_NOTES.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label className={sectionLabel}>Mode</Label>
                <Select
                  value={compareMode}
                  onValueChange={(v) => {
                    setCompareMode(v);
                    setCompareTypes(new Set(v === "scale" ? ["major"] : ["maj"]));
                  }}
                >
                  <SelectTrigger
                    data-testid="compare-mode-select"
                    className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-12 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="scale">Scales</SelectItem>
                    <SelectItem value="chord">Chords</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                data-testid="compare-export-button"
                onClick={handleCompareExport}
                disabled={pdfBusy}
                className="rounded-xl bg-gray-900 hover:bg-gray-700 text-white h-12 font-medium w-full"
              >
                <FileText className="w-4 h-4 mr-2" />
                {pdfBusy ? "Building…" : "Download comparison PDF"}
              </Button>
            </div>

            <Label className={sectionLabel}>
              Pick {compareMode === "scale" ? "scales" : "chords"} to include
            </Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(compareMode === "scale" ? SCALES : CHORDS).map(([k, v]) => {
                const on = compareTypes.has(k);
                const isFree = compareMode === "scale" ? isScaleFree(k) : isChordFree(k);
                const locked = !paid && !isFree;
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`compare-type-${k}`}
                    onClick={() => toggleCompareType(k)}
                    className={`px-2 py-1 border-2 text-xs font-bold inline-flex items-center gap-1 ${
                      on
                        ? "bg-gray-900 text-white border-gray-900"
                        : locked
                        ? "bg-white text-gray-400 border-gray-200"
                        : "bg-white text-gray-900 border-gray-300"
                    }`}
                  >
                    {v.label}
                    {locked && <Lock className="w-3 h-3 text-[#673A80]" />}
                  </button>
                );
              })}
              <button
                type="button"
                data-testid="compare-type-clear"
                onClick={() => setCompareTypes(new Set())}
                className="px-2 py-1 border-2 text-xs font-mono uppercase border-gray-300 hover:border-gray-900"
              >
                clear
              </button>
            </div>
          </section>

          {/* Circle of Fifths + progression builder */}
          <section
            className="rounded-2xl border-2 border-gray-900 bg-white p-6 md:p-8 relative overflow-hidden"
            data-testid="circle-panel"
          >
            <div className="flex items-center gap-2 mb-4">
              <Music className="w-4 h-4 text-gray-700" />
              <h2 className="text-sm uppercase tracking-[0.2em] font-semibold text-gray-900">
                Circle of Fifths — build a progression
              </h2>
              {!paid && (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-white bg-[#673A80] px-2 py-0.5 rounded-full">
                  <Lock className="w-3 h-3" /> Paid
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed max-w-2xl">
              Click any wedge to add a chord to your progression. Outer = major,
              middle = minor, inner = diminished. Press Play to hear it back.
            </p>
            <div
              className={paid ? "grid grid-cols-1 md:grid-cols-2 gap-6 items-start" : "grid grid-cols-1 md:grid-cols-2 gap-6 items-start pointer-events-none blur-[3px] select-none"}
              aria-hidden={!paid}
            >
              <div className="flex justify-center">
                <CircleOfFifths onPick={(c) => paid && setProgression((p) => [...p, c])} />
              </div>
              <div className="flex flex-col gap-4">
                <div>
                  <Label className={sectionLabel}>Your progression</Label>
                  <div
                    className="mt-2 flex flex-wrap gap-2 min-h-[60px] p-3 border-2 border-gray-300 rounded-xl"
                    data-testid="progression-list"
                  >
                    {progression.length === 0 && (
                      <span className="text-sm text-gray-400">Click chords in the circle…</span>
                    )}
                    {progression.map((c, i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`progression-chord-${i}`}
                        onClick={() =>
                          setProgression((p) => p.filter((_, idx) => idx !== i))
                        }
                        className="px-3 py-1 border-2 border-gray-900 rounded-xl font-mono font-medium text-sm hover:bg-gray-100"
                        title="Click to remove"
                      >
                        {c.label} ×
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="progression-play-button"
                    onClick={handlePlayProgression}
                    className="rounded-xl bg-gray-900 hover:bg-gray-700 text-white h-11 px-5 font-medium"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Play progression
                  </Button>
                  <Button
                    data-testid="progression-stop-button"
                    onClick={() => stopAll()}
                    variant="outline"
                    className="rounded-xl border-2 border-gray-900 h-11 px-3"
                    aria-label="Stop"
                  >
                    <Square className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid="progression-clear-button"
                    onClick={() => setProgression([])}
                    variant="outline"
                    className="rounded-xl border-2 border-gray-300 h-11 px-4 font-medium"
                  >
                    Clear
                  </Button>
                </div>

                {/* Save / load progression */}
                <div className="flex flex-col gap-2 pt-2 border-t border-gray-200">
                  <Label className={sectionLabel}>Save progression</Label>
                  {savedProgressions.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Select
                        value={selectedProgression}
                        onValueChange={handleLoadProgression}
                      >
                        <SelectTrigger
                          data-testid="progression-select"
                          className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-11 bg-white flex-1"
                        >
                          <SelectValue placeholder="Load saved…" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          {savedProgressions.map((p) => (
                            <SelectItem key={p.name} value={p.name}>
                              {p.name} · {p.chords.length} chords
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        data-testid="progression-delete-button"
                        onClick={handleDeleteProgression}
                        disabled={!selectedProgression}
                        variant="outline"
                        className="rounded-xl border-2 border-gray-300 h-11 px-3"
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="progression-name-input"
                      placeholder="e.g. ii-V-I in C"
                      value={progressionName}
                      onChange={(e) => setProgressionName(e.target.value)}
                      className="rounded-xl border-2 border-gray-300 focus:border-gray-900 h-11 flex-1"
                    />
                    <Button
                      data-testid="progression-save-button"
                      onClick={handleSaveProgression}
                      className="rounded-xl bg-gray-900 hover:bg-gray-700 text-white h-11 px-4 font-medium"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            {!paid && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px]"
                data-testid="circle-locked-overlay"
              >
                <div className="flex flex-col items-center gap-3 text-center max-w-xs px-4">
                  <div className="w-12 h-12 rounded-full bg-[#673A80] text-white flex items-center justify-center">
                    <Lock className="w-6 h-6" />
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    Circle of Fifths is a paid feature
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Unlock lifetime access to build & play progressions on the
                    interactive Circle of Fifths.
                  </p>
                  <Button
                    data-testid="circle-unlock-button"
                    onClick={openPaywall}
                    className="rounded-xl bg-[#673A80] hover:bg-[#552d6b] text-white h-10 px-4 font-medium mt-1"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Unlock ₹399
                  </Button>
                </div>
              </div>
            )}
          </section>


          <div className="text-xs text-gray-500 leading-relaxed max-w-2xl">
            Pick a mode, root note and quality on the left. Adjust the octave
            range, toggle labels, and pick brand colors. Use inversions to teach
            voice leading, or batch-export a full diatonic set for a lesson.
          </div>
        </main>
      </div>
    </div>
  );
}
